import React, { useState, useMemo } from 'react';
import {
  Table, Card, Input, Button, Space, Typography, Form, Select, Modal,
  Drawer, Descriptions, Alert, Upload, message,
} from 'antd';
import { SearchOutlined, ReloadOutlined, PlusOutlined, DownloadOutlined, InboxOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

// ──────────────────────────────────────────────
// Mock 数据 — TODO: 联调时替换为接口数据
// ──────────────────────────────────────────────

const PLAN_PRODUCTS = [
  { value: 'P_ECO_001', label: '智能云台摄像机方案（P_ECO_001）' },
  { value: 'P_ECO_002', label: '4G低功耗电池摄像机方案（P_ECO_002）' },
  { value: 'P_ECO_003', label: '智能门铃方案（P_ECO_003）' },
];

const MACHINE_PRODUCTS = {
  P_ECO_001: [
    { value: 'P_DRV_001', label: '智能云台摄像机-康林高配版（P_DRV_001）', companyName: '东莞康林物联网有限公司', companyId: 'ENT_C01' },
    { value: 'P_DRV_002', label: '智能云台摄像机-云视标准版（P_DRV_002）', companyName: '成都云视科技有限公司', companyId: 'ENT_C02' },
  ],
  P_ECO_002: [
    { value: 'P_DRV_004', label: '4G电池摄像机-康林版（P_DRV_004）', companyName: '东莞康林物联网有限公司', companyId: 'ENT_C01' },
  ],
  P_ECO_003: [
    { value: 'P_DRV_005', label: '智能门铃-云视定制版（P_DRV_005）', companyName: '成都云视科技有限公司', companyId: 'ENT_C02' },
  ],
};

// License 归属（方案产品 → 所属 License）
const PLAN_LICENSES = {
  P_ECO_001: ['A01M012604210133', 'A01M012604210267', 'A01M012604220288'],
  P_ECO_002: ['A01M012604230310', 'A01M012604230311'],
  P_ECO_003: ['A02V012605010199', 'A02V012605010200'],
};

const MOCK_RECORDS = [
  {
    key: 'GH20260421101233123A07', operationName: '康林高配 4 月批次改号',
    planProductName: '智能云台摄像机方案', planProductId: 'P_ECO_001',
    machineProductName: '智能云台摄像机-康林高配版', machineProductId: 'P_DRV_001',
    machineCompanyName: '东莞康林物联网有限公司', machineCompanyId: 'ENT_C01',
    operateTime: '2026-04-21 10:12:33', operatorName: '宋道宗', operatorAccount: 'songdz',
    licenses: ['A01M012604210133', 'A01M012604210267'],
  },
  {
    key: 'GH20260422142010188B12', operationName: '云视标准版改号',
    planProductName: '智能云台摄像机方案', planProductId: 'P_ECO_001',
    machineProductName: '智能云台摄像机-云视标准版', machineProductId: 'P_DRV_002',
    machineCompanyName: '成都云视科技有限公司', machineCompanyId: 'ENT_C02',
    operateTime: '2026-04-22 14:20:10', operatorName: '宋道宗', operatorAccount: 'songdz',
    licenses: ['A01M012604220288'],
  },
  {
    key: 'GH20260425163005256C33', operationName: '4G 康林版改号',
    planProductName: '4G低功耗电池摄像机方案', planProductId: 'P_ECO_002',
    machineProductName: '4G电池摄像机-康林版', machineProductId: 'P_DRV_004',
    machineCompanyName: '东莞康林物联网有限公司', machineCompanyId: 'ENT_C01',
    operateTime: '2026-04-25 16:30:05', operatorName: '李工', operatorAccount: 'ligong',
    licenses: ['A01M012604230310'],
  },
];

const pad = (n, len = 2) => String(n).padStart(len, '0');

const genOperationNo = () => {
  const d = new Date();
  const stamp = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}${pad(d.getMilliseconds(), 3)}`;
  const rand = pad(Math.floor(Math.random() * 1000), 3);
  return `GH${stamp}${rand}`;
};

const toCSV = (rows) => rows.map((r) => r.map((c) => `"${String(c == null ? '' : c).replace(/"/g, '""')}"`).join(',')).join('\n');

const triggerDownload = (filename, content) => {
  const blob = new Blob(['\ufeff' + content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const ManualChange = () => {
  const [records, setRecords] = useState(MOCK_RECORDS);

  // 查询条件
  const [form] = Form.useForm();
  const [planMode, setPlanMode] = useState('name');
  const [machineMode, setMachineMode] = useState('name');
  const [applied, setApplied] = useState({ license: '', plan: '', planMode: 'name', machine: '', machineMode: 'name', opNo: '' });

  // 发起改号
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm] = Form.useForm();
  const [planId, setPlanId] = useState(undefined);
  const [machineId, setMachineId] = useState(undefined);
  const [parsed, setParsed] = useState(null);

  // 明细
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);

  const machineOptions = useMemo(
    () => (planId ? (MACHINE_PRODUCTS[planId] || []) : []),
    [planId],
  );

  const selectedMachine = useMemo(
    () => machineOptions.find((m) => m.value === machineId),
    [machineOptions, machineId],
  );

  const filtered = useMemo(() => {
    let list = records;
    if (applied.license.trim()) {
      const kw = applied.license.trim().toLowerCase();
      list = list.filter((r) => r.licenses.some((l) => l.toLowerCase().includes(kw)));
    }
    if (applied.plan.trim()) {
      const kw = applied.plan.trim().toLowerCase();
      list = list.filter((r) => (applied.planMode === 'id'
        ? r.planProductId.toLowerCase().includes(kw)
        : r.planProductName.toLowerCase().includes(kw)));
    }
    if (applied.machine.trim()) {
      const kw = applied.machine.trim().toLowerCase();
      list = list.filter((r) => (applied.machineMode === 'id'
        ? r.machineProductId.toLowerCase().includes(kw)
        : r.machineProductName.toLowerCase().includes(kw)));
    }
    if (applied.opNo.trim()) {
      list = list.filter((r) => r.key.toLowerCase().includes(applied.opNo.trim().toLowerCase()));
    }
    return list;
  }, [records, applied]);

  const handleSearch = () => {
    const v = form.getFieldsValue();
    setApplied({
      license: v.license || '',
      plan: v.plan || '',
      planMode,
      machine: v.machine || '',
      machineMode,
      opNo: v.opNo || '',
    });
  };

  const handleReset = () => {
    form.resetFields();
    setPlanMode('name');
    setMachineMode('name');
    setApplied({ license: '', plan: '', planMode: 'name', machine: '', machineMode: 'name', opNo: '' });
  };

  const openCreate = () => {
    createForm.resetFields();
    setPlanId(undefined);
    setMachineId(undefined);
    setParsed(null);
    setCreateOpen(true);
  };

  const openDetail = (record) => {
    setDetailRecord(record);
    setDetailOpen(true);
  };

  const handleFileChange = (info) => {
    const file = info.file;
    if (!file) return;
    if (!planId) {
      message.warning('请先选择方案产品');
      return;
    }
    // Mock 解析：取该方案下 License，并附带一条不属于该方案的 License 演示校验
    const valid = PLAN_LICENSES[planId] || [];
    const invalid = ['A01M012604999999'];
    setParsed({ valid, invalid });
    message.success(`已解析 ${valid.length + invalid.length} 条 License`);
  };

  const handleCreate = async () => {
    try {
      await createForm.validateFields();
    } catch {
      return;
    }
    if (!selectedMachine) {
      message.warning('请选择整机产品');
      return;
    }
    if (!parsed) {
      message.warning('请上传 License 文件');
      return;
    }
    if (parsed.invalid.length > 0) {
      message.error('存在不属于所选方案产品的 License，禁止提交');
      return;
    }
    const newRecord = {
      key: genOperationNo(),
      operationName: createForm.getFieldValue('operationName'),
      planProductName: PLAN_PRODUCTS.find((p) => p.value === planId)?.label.split('（')[0] || '',
      planProductId: planId,
      machineProductName: selectedMachine.label.split('（')[0],
      machineProductId: selectedMachine.value,
      machineCompanyName: selectedMachine.companyName,
      machineCompanyId: selectedMachine.companyId,
      operateTime: new Date().toLocaleString('zh-CN'),
      operatorName: '当前登录人',
      operatorAccount: 'current_user',
      licenses: parsed.valid,
    };
    setRecords((prev) => [newRecord, ...prev]);
    setCreateOpen(false);
    message.success(`改号操作已保存，操作编号：${newRecord.key}`);
  };

  const handleDownloadTemplate = () => {
    triggerDownload('改号License导入模板.csv', toCSV([['License'], ['A01M012604210133'], ['A01M012604210267']]));
  };

  const handleExportDetail = (record) => {
    const header = ['方案产品', '方案ID', '整机产品', '产品ID', 'License'];
    const rows = record.licenses.map((l) => [record.planProductName, record.planProductId, record.machineProductName, record.machineProductId, l]);
    triggerDownload(`改号操作_${record.key}.csv`, toCSV([header, ...rows]));
  };

  const columns = [
    {
      title: '操作编号', dataIndex: 'key', width: 250,
      render: (t, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.operationName}</div>
          <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>{t}</Text>
        </div>
      ),
    },
    {
      title: '方案产品', key: 'plan', width: 220,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.planProductName}</div>
          <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>{r.planProductId}</Text>
        </div>
      ),
    },
    {
      title: '整机产品', key: 'machine', width: 240,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.machineProductName}</div>
          <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>{r.machineProductId}</Text>
        </div>
      ),
    },
    {
      title: '整机企业', key: 'company', width: 220,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.machineCompanyName}</div>
          <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>{r.machineCompanyId}</Text>
        </div>
      ),
    },
    { title: '操作时间', dataIndex: 'operateTime', width: 170 },
    {
      title: '操作人', key: 'operator', width: 160,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.operatorName}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{r.operatorAccount}</Text>
        </div>
      ),
    },
    {
      title: '操作', key: 'action', width: 100, fixed: 'right',
      render: (_, r) => <Button type="link" size="small" onClick={() => openDetail(r)}>查看明细</Button>,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4} style={{ margin: 0 }}>发起改号</Title>
      </div>

      <Alert
        type="info"
        showIcon
        closable
        style={{ marginBottom: 16 }}
        message="后台发起改号：选择方案产品与整机产品后上传 License，系统校验 License 是否属于所选方案产品；校验通过后更新设备的整机产品 ID，并生成操作编号。"
      />

      <Card bordered={false} style={{ marginBottom: 16, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
        <Form form={form} layout="inline" style={{ rowGap: 12 }}>
          <Form.Item name="license" style={{ marginBottom: 0 }}>
            <Input placeholder="请输入 License" allowClear style={{ width: 180 }} />
          </Form.Item>
          <Form.Item name="plan" style={{ marginBottom: 0 }}>
            <Space.Compact>
              <Select value={planMode} onChange={setPlanMode} style={{ width: 100 }}
                options={[{ value: 'name', label: '方案产品' }, { value: 'id', label: '方案ID' }]} />
              <Input placeholder={planMode === 'id' ? '请输入方案 ID' : '请输入方案产品'} allowClear style={{ width: 170 }} />
            </Space.Compact>
          </Form.Item>
          <Form.Item name="machine" style={{ marginBottom: 0 }}>
            <Space.Compact>
              <Select value={machineMode} onChange={setMachineMode} style={{ width: 100 }}
                options={[{ value: 'name', label: '整机产品' }, { value: 'id', label: '产品ID' }]} />
              <Input placeholder={machineMode === 'id' ? '请输入产品 ID' : '请输入整机产品'} allowClear style={{ width: 170 }} />
            </Space.Compact>
          </Form.Item>
          <Form.Item name="opNo" style={{ marginBottom: 0 }}>
            <Input placeholder="请输入操作编号" allowClear style={{ width: 190 }} />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>查询</Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card bordered={false} style={{ borderRadius: 12 }}>
        <div style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>发起改号</Button>
        </div>
        <Table
          columns={columns}
          dataSource={filtered}
          rowKey="key"
          scroll={{ x: 1500 }}
          style={{ whiteSpace: 'nowrap' }}
          pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }}
          locale={{ emptyText: '暂无改号操作记录' }}
        />
      </Card>

      {/* 发起改号 */}
      <Modal
        title="发起改号"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={handleCreate}
        okText="保存"
        cancelText="取消"
        width={620}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item name="operationName" label="操作名称" rules={[{ required: true, message: '请输入操作名称' }]}>
            <Input placeholder="请输入操作名称" maxLength={50} showCount />
          </Form.Item>
          <Form.Item name="planProduct" label="方案产品" rules={[{ required: true, message: '请选择方案产品' }]}>
            <Select
              placeholder="请选择方案产品"
              options={PLAN_PRODUCTS}
              onChange={(val) => {
                setPlanId(val);
                setMachineId(undefined);
                setParsed(null);
                createForm.setFieldsValue({ machineProduct: undefined });
              }}
            />
          </Form.Item>
          <Form.Item name="machineProduct" label="整机产品" rules={[{ required: true, message: '请选择整机产品' }]}>
            <Select
              placeholder={planId ? '请选择整机产品' : '请先选择方案产品'}
              disabled={!planId}
              options={machineOptions}
              onChange={(val) => {
                setMachineId(val);
                setParsed(null);
              }}
            />
          </Form.Item>
          <Form.Item label="整机企业">
            <Input
              disabled
              value={selectedMachine ? `${selectedMachine.companyName}（${selectedMachine.companyId}）` : ''}
              placeholder="选择整机产品后自动带出"
            />
          </Form.Item>

          <div style={{ marginBottom: 8 }}>
            <Space>
              <Text type="secondary">导入格式：CSV / Excel，单次最多 5000 条</Text>
              <Button type="link" size="small" icon={<DownloadOutlined />} style={{ padding: 0 }} onClick={handleDownloadTemplate}>下载模板</Button>
            </Space>
          </div>
          <Upload.Dragger
            accept=".xls,.xlsx,.csv"
            maxCount={1}
            beforeUpload={() => false}
            onChange={handleFileChange}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
            <p className="ant-upload-hint">仅支持 .xls / .xlsx / .csv 文件</p>
          </Upload.Dragger>

          {parsed && (
            <div style={{ marginTop: 12 }}>
              <Space size={16}>
                <Text>可改号：<Text strong style={{ color: '#52c41a' }}>{parsed.valid.length}</Text> 条</Text>
                <Text>不属于方案（禁止）：<Text strong style={{ color: '#ff4d4f' }}>{parsed.invalid.length}</Text> 条</Text>
              </Space>
              {parsed.invalid.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <Text type="danger" style={{ fontSize: 12 }}>以下 License 不属于所选方案产品，请移除后再提交：</Text>
                  <div style={{ marginTop: 4, fontFamily: 'monospace', fontSize: 12 }}>{parsed.invalid.join('、')}</div>
                </div>
              )}
            </div>
          )}
        </Form>
      </Modal>

      {/* 明细 */}
      <Drawer
        title="改号操作明细"
        width={760}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        destroyOnClose
      >
        {detailRecord && (
          <>
            <Descriptions bordered column={2} size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="操作编号" span={2}>
                <Text copyable style={{ fontFamily: 'monospace' }}>{detailRecord.key}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="操作名称" span={2}>{detailRecord.operationName}</Descriptions.Item>
              <Descriptions.Item label="方案产品">{detailRecord.planProductName}</Descriptions.Item>
              <Descriptions.Item label="方案ID">{detailRecord.planProductId}</Descriptions.Item>
              <Descriptions.Item label="整机产品">{detailRecord.machineProductName}</Descriptions.Item>
              <Descriptions.Item label="产品ID">{detailRecord.machineProductId}</Descriptions.Item>
              <Descriptions.Item label="整机企业">{detailRecord.machineCompanyName}</Descriptions.Item>
              <Descriptions.Item label="整机企业ID">{detailRecord.machineCompanyId}</Descriptions.Item>
              <Descriptions.Item label="操作时间">{detailRecord.operateTime}</Descriptions.Item>
              <Descriptions.Item label="操作人">{detailRecord.operatorName}（{detailRecord.operatorAccount}）</Descriptions.Item>
            </Descriptions>

            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong>License 列表（{detailRecord.licenses.length}）</Text>
              <Button size="small" icon={<DownloadOutlined />} onClick={() => handleExportDetail(detailRecord)}>导出</Button>
            </div>
            <Table
              size="small"
              rowKey={(r) => r.license}
              dataSource={detailRecord.licenses.map((l) => ({ license: l }))}
              pagination={false}
              columns={[
                { title: 'License', dataIndex: 'license', render: (t) => <Text copyable style={{ fontFamily: 'monospace' }}>{t}</Text> },
              ]}
            />
          </>
        )}
      </Drawer>
    </div>
  );
};

export default ManualChange;
